import React, { useCallback, useEffect, useState, useRef } from 'react';
import { PlusIcon, SearchIcon, FileTextIcon, SendIcon, ChevronDownIcon, XIcon, LoaderIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { DeleteConfirmationModal } from '../DeleteConfirmationModal';
import { getTeacherStudents } from '../../services/studentService';
import { getTeacherInvoices, createInvoice, updateInvoice, deleteInvoice } from '../../services/invoiceService';
import toast from 'react-hot-toast';
export const InvoicePage = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser,
    loading: authLoading
  } = useAuth();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [hoveredStatusOption, setHoveredStatusOption] = useState<string | null>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const statusOptions = ['All', 'Paid', 'Pending'];
  // Students State
  const [students, setStudents] = useState<any[]>([]);
  const [isStudentsLoading, setIsStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  // Drawer State
  const [drawerInvoice, setDrawerInvoice] = useState<any | null>(null);
  const [drawerMode, setDrawerMode] = useState<'edit' | 'create'>('edit');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerAnimation, setDrawerAnimation] = useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const [drawerFormValues, setDrawerFormValues] = useState({
    studentId: '',
    studentName: '',
    amount: '',
    date: '',
    status: 'Pending',
    billingType: 'Monthly' as 'Monthly' | 'Per Lesson',
    billingPeriod: '',
    lessonDate: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    studentId?: string;
    amount?: string;
  }>({});
  // Student Dropdown State
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const studentDropdownRef = useRef<HTMLDivElement>(null);
  const [isDrawerStatusDropdownOpen, setIsDrawerStatusDropdownOpen] = useState(false);
  const [hoveredDrawerStatusOption, setHoveredDrawerStatusOption] = useState<string | null>(null);
  const drawerStatusDropdownRef = useRef<HTMLDivElement>(null);
  const drawerStatusOptions = ['Paid', 'Pending'];
  // Billing Type dropdown state
  const [isBillingTypeDropdownOpen, setIsBillingTypeDropdownOpen] = useState(false);
  const [hoveredBillingTypeOption, setHoveredBillingTypeOption] = useState<string | null>(null);
  const billingTypeDropdownRef = useRef<HTMLDivElement>(null);
  const billingTypeOptions = ['Monthly', 'Per Lesson'];
  // Fetch Invoices
  const fetchInvoices = useCallback(async () => {
    if (authLoading) return;
    if (!currentUser) {
      setIsInvoicesLoading(false);
      return;
    }
    setIsInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const fetchedInvoices = await getTeacherInvoices(currentUser.uid);
      setInvoices(fetchedInvoices);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setInvoicesError('Failed to load invoices.');
    } finally {
      setIsInvoicesLoading(false);
    }
  }, [currentUser, authLoading]);
  // Fetch Students
  const fetchStudents = useCallback(async () => {
    if (authLoading) return;
    if (!currentUser) {
      setIsStudentsLoading(false);
      return;
    }
    setIsStudentsLoading(true);
    setStudentsError(null);
    try {
      const fetchedStudents = await getTeacherStudents(currentUser.uid);
      setStudents(fetchedStudents);
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudentsError('Failed to load students.');
    } finally {
      setIsStudentsLoading(false);
    }
  }, [currentUser, authLoading]);
  useEffect(() => {
    if (!authLoading) {
      fetchStudents();
      fetchInvoices();
    }
  }, [fetchStudents, fetchInvoices, authLoading]);
  // Handle click outside for status dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
      if (drawerStatusDropdownRef.current && !drawerStatusDropdownRef.current.contains(event.target as Node)) {
        setIsDrawerStatusDropdownOpen(false);
      }
      if (billingTypeDropdownRef.current && !billingTypeDropdownRef.current.contains(event.target as Node)) {
        setIsBillingTypeDropdownOpen(false);
      }
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target as Node)) {
        setIsStudentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Handle drawer animation states
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isDrawerOpen && drawerAnimation !== 'entered') {
      setDrawerAnimation('entering');
      timer = setTimeout(() => {
        setDrawerAnimation('entered');
      }, 50);
    } else if (!isDrawerOpen && drawerAnimation !== 'exited') {
      setDrawerAnimation('exiting');
      timer = setTimeout(() => {
        setDrawerAnimation('exited');
      }, 300);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isDrawerOpen, drawerAnimation]);
  // Handle Escape key to close drawer
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDrawerOpen) {
        closeDrawer();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [drawerInvoice]);
  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.studentName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const handleRowClick = (invoice: any) => {
    setSelectedInvoiceId(selectedInvoiceId === invoice.id ? null : invoice.id);
    setDrawerInvoice(invoice);
    setDrawerMode('edit');
    setDrawerFormValues({
      studentId: invoice.studentId || '',
      studentName: invoice.studentName,
      amount: invoice.amount.toString(),
      date: invoice.date,
      status: invoice.status,
      billingType: invoice.billingType || 'Monthly',
      billingPeriod: invoice.billingPeriod || '',
      lessonDate: invoice.lessonDate || ''
    });
    setIsDrawerOpen(true);
  };
  const openCreateDrawer = () => {
    setDrawerInvoice(null);
    setDrawerMode('create');
    setDrawerFormValues({
      studentId: '',
      studentName: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      status: 'Pending',
      billingType: 'Monthly',
      billingPeriod: '',
      lessonDate: ''
    });
    setStudentSearchQuery('');
    setFormErrors({});
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerInvoice(null);
  };
  const handleDrawerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setDrawerFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error on change
    if (name === 'amount' && formErrors.amount) {
      setFormErrors(prev => ({
        ...prev,
        amount: undefined
      }));
    }
  };
  const handleDrawerSave = async () => {
    if (!currentUser) return;
    // Field-level validation
    const errors: {
      studentId?: string;
      amount?: string;
    } = {};
    if (!drawerFormValues.studentId) {
      errors.studentId = 'Please select a student';
    }
    if (!drawerFormValues.amount || parseFloat(drawerFormValues.amount) <= 0) {
      errors.amount = 'Please enter an amount';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setIsSaving(true);
    try {
      const invoiceData = {
        studentId: drawerFormValues.studentId,
        studentName: drawerFormValues.studentName,
        amount: parseFloat(drawerFormValues.amount) || 0,
        date: drawerFormValues.date,
        status: drawerFormValues.status as 'Paid' | 'Pending',
        billingType: drawerFormValues.billingType,
        billingPeriod: drawerFormValues.billingPeriod,
        lessonDate: drawerFormValues.lessonDate
      };
      if (drawerMode === 'create') {
        await createInvoice(currentUser.uid, invoiceData);
        toast.success('Invoice created successfully');
      } else if (drawerMode === 'edit' && drawerInvoice) {
        await updateInvoice(currentUser.uid, drawerInvoice.id, invoiceData);
        toast.success('Invoice updated successfully');
      }
      // Refresh list and close
      await fetchInvoices();
      closeDrawer();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error(drawerMode === 'create' ? 'Failed to create invoice' : 'Failed to update invoice');
    } finally {
      setIsSaving(false);
    }
  };
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const handleDrawerDelete = () => {
    if (!currentUser || drawerMode !== 'edit' || !drawerInvoice) return;
    setIsDeleteModalOpen(true);
  };
  const confirmDelete = async () => {
    if (!currentUser || !drawerInvoice) return;
    setIsSaving(true);
    try {
      await deleteInvoice(currentUser.uid, drawerInvoice.id);
      toast.success('Invoice deleted successfully');
      setIsDeleteModalOpen(false);
      await fetchInvoices();
      closeDrawer();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    } finally {
      setIsSaving(false);
    }
  };
  return <>
      {/* Hover animation styles matching Assigned Tasks page */}
      <style data-id="element-5457">{`
        .invoice-row {
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
          will-change: transform;
          transform: translateZ(0);
          backface-visibility: hidden;
        }

        html:not(.dark) .invoice-row {
          background-color: white;
        }

        html:not(.dark) .invoice-row:hover {
          transform: translateY(-2px);
          background: rgba(0, 0, 0, 0.02);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        html.dark .invoice-row {
          background-color: transparent;
        }

        html.dark .invoice-row:hover {
          transform: translateY(-2px);
          background: rgba(255, 255, 255, 0.06);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        }

        .invoice-row.selected {
          transform: translateY(-1px);
        }

        html:not(.dark) .invoice-row.selected {
          background: #eff6ff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        html.dark .invoice-row.selected {
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        }

        .invoice-search-container {
          transition: border-color 0.2s ease;
        }

        html:not(.dark) .invoice-search-container:hover {
          border-color: #9ca3af;
        }

        html:not(.dark) .invoice-search-container:focus-within {
          border-color: #6b7280;
        }

        html.dark .invoice-search-container:hover {
          border-color: #9ca3af;
        }

        html.dark .invoice-search-container:focus-within {
          border-color: #d1d5db;
        }

        .invoice-dropdown-trigger {
          transition: box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
        }

        .invoice-dropdown-trigger:hover {
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
        }

        html:not(.dark) .invoice-dropdown-trigger:hover {
          border-color: #6b7280;
        }

        html.dark .invoice-dropdown-trigger:hover {
          border-color: rgba(255, 255, 255, 0.4);
        }

        .invoice-dropdown-item {
          transition: background-color 0.2s ease, color 0.2s ease;
        }

        .invoice-send-btn {
          transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
        }

        .invoice-send-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        }

        .invoice-send-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        .invoice-create-btn {
          transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
        }

        .invoice-create-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .invoice-create-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>

      <div className="mx-auto" data-id="element-5458">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4" data-id="element-5459">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5460">
            Invoices
          </h1>
          <button onClick={openCreateDrawer} className={`invoice-create-btn flex items-center px-4 py-2 border-2 rounded-md font-medium ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-5461">
            Create Invoice
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-5462">
          <div className={`invoice-search-container relative flex-1 max-w-md flex items-center rounded-lg px-3 shadow-sm ${isDark ? 'bg-navy-800 border border-gray-500' : 'bg-white border border-gray-300'}`} data-id="element-5463">
            <SearchIcon className={`h-5 w-5 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5464" />
            <input type="text" placeholder="Search by student name" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full p-2.5 bg-transparent border-none focus:ring-0 focus:outline-none sm:text-sm ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'}`} data-id="element-5465" />
          </div>
          <div className="relative w-48 flex-shrink-0" ref={statusDropdownRef} data-id="element-5466">
            <button type="button" onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)} style={{
            color: isDark ? '#ffffff' : '#111827'
          }} className={`invoice-dropdown-trigger flex items-center justify-between w-full pl-3 pr-3 py-2.5 border rounded-lg focus:outline-none focus:ring-0 sm:text-sm appearance-none ${isDark ? 'bg-navy-800 border-gray-500 hover:border-gray-400 focus:border-gray-300' : 'bg-white border-gray-300 hover:border-gray-400 focus:border-gray-500'}`} data-id="element-5467">
              <span style={{
              color: isDark ? '#ffffff' : '#111827'
            }} data-id="element-5468">
                {statusFilter === 'All' ? 'All Statuses' : statusFilter}
              </span>
              <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5469" />
            </button>
            {isStatusDropdownOpen && <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-300'}`} onMouseLeave={() => setHoveredStatusOption(null)} data-id="element-5470">
                {statusOptions.map(option => {
              const isHovered = hoveredStatusOption === option;
              const isSelected = statusFilter === option;
              const showHighlight = hoveredStatusOption !== null ? isHovered : isSelected;
              return <button key={option} type="button" onMouseEnter={() => setHoveredStatusOption(option)} onClick={() => {
                setStatusFilter(option);
                setIsStatusDropdownOpen(false);
                setHoveredStatusOption(null);
              }} className={`invoice-dropdown-item block w-full text-left px-3 py-2.5 sm:text-sm first:rounded-t-lg last:rounded-b-lg ${showHighlight ? isDark ? 'bg-white/10 text-white font-medium' : 'bg-gray-100 text-gray-900 font-medium' : isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5471">
                      {option === 'All' ? 'All Statuses' : option}
                    </button>;
            })}
              </div>}
          </div>
        </div>

        {/* Table / List */}
        <div className={`rounded-xl shadow-sm overflow-hidden ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5472">
          {isInvoicesLoading ? <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-id="element-5473">
              <LoaderIcon className={`h-8 w-8 animate-spin mb-4 ${isDark ? 'text-[#10B981]' : 'text-[#10B981]'}`} data-id="element-5474" />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5475">
                Loading invoices...
              </p>
            </div> : invoicesError ? <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-id="element-5476">
              <div className={`p-4 rounded-full mb-4 ${isDark ? 'bg-red-900/30' : 'bg-red-50'}`} data-id="element-5477">
                <XIcon className={`w-8 h-8 ${isDark ? 'text-red-400' : 'text-red-500'}`} data-id="element-5478" />
              </div>
              <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5479">
                Error Loading Invoices
              </h3>
              <p className={`mb-6 max-w-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5480">
                {invoicesError}
              </p>
              <button onClick={fetchInvoices} className={`px-4 py-2 border-2 rounded-md font-medium transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-5481">
                Try Again
              </button>
            </div> : filteredInvoices.length > 0 ? <div className="overflow-x-auto" data-id="element-5482">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-700 table-fixed" data-id="element-5483">
                <thead className={isDark ? 'bg-navy-800' : 'bg-gray-50'} data-id="element-5484">
                  <tr data-id="element-5485">
                    <th scope="col" className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5486">
                      Student Name
                    </th>
                    <th scope="col" className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5487">
                      Amount
                    </th>
                    <th scope="col" className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5488">
                      Date
                    </th>
                    <th scope="col" className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5489">
                      Status
                    </th>
                    <th scope="col" className={`px-6 py-4 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5490">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-navy-700' : 'divide-gray-200'}`} data-id="element-5491">
                  {filteredInvoices.map(invoice => <tr key={invoice.id} onClick={() => handleRowClick(invoice)} className={`invoice-row cursor-pointer ${selectedInvoiceId === invoice.id ? 'selected' : ''}`} data-id="element-5492">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5493">
                        {invoice.studentName}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-5494">
                        ${invoice.amount.toFixed(2)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-5495">
                        {invoice.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5496">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invoice.status === 'Paid' ? isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800' : isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-5497">
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5498">
                        <div className="flex justify-end" data-id="element-5499">
                          {invoice.status === 'Pending' && <button onClick={e => {
                      e.stopPropagation();
                    }} className={`invoice-send-btn flex items-center px-3 py-1 rounded-md text-sm font-medium ${isDark ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-5500">
                              <SendIcon className="w-4 h-4 mr-1" data-id="element-5501" />
                              Send
                            </button>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-id="element-5502">
              <div className={`p-4 rounded-full mb-4 ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-5503">
                <FileTextIcon className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5504" />
              </div>
              <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-5505">
                No invoices yet
              </h3>
              <p className={`mb-6 max-w-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5506">
                {searchQuery || statusFilter !== 'All' ? 'No invoices match your current filters. Try adjusting them.' : "You haven't created any invoices yet. Get started by creating your first one."}
              </p>
              {!(searchQuery || statusFilter !== 'All') && <button className={`invoice-create-btn flex items-center px-4 py-2 border-2 rounded-md font-medium ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} onClick={openCreateDrawer} data-id="element-5507">
                  Create Your First Invoice
                </button>}
            </div>}
        </div>
      </div>

      {/* Invoice Detail Drawer */}
      <div className={`fixed inset-0 z-50 ${drawerAnimation === 'exited' ? 'pointer-events-none' : ''}`} aria-modal="true" role="dialog" data-id="element-5508">
        {/* Overlay */}
        <div className={`absolute inset-0 bg-black transition-opacity duration-300 ${drawerAnimation === 'entering' || drawerAnimation === 'entered' ? 'opacity-50' : 'opacity-0'}`} onClick={e => {
        if (e.target === e.currentTarget) {
          closeDrawer();
        }
      }} data-id="element-5509" />

        {/* Panel */}
        <div className={`absolute inset-y-0 right-0 w-full max-w-[480px] ${isDark ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} shadow-xl flex flex-col transform transition-transform duration-500 ${drawerAnimation === 'entering' || drawerAnimation === 'entered' ? 'translate-x-0' : 'translate-x-full'} md:rounded-l-2xl`} data-id="element-5510">
          {/* Header */}
          <div className={`px-6 py-4 flex items-center justify-between border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-5511">
            <div data-id="element-5512">
              <h2 className="text-xl font-semibold" data-id="element-5513">
                {drawerMode === 'create' ? 'Create Invoice' : 'Edit Invoice'}
              </h2>
              {drawerMode === 'edit' && drawerInvoice && <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5514">
                  {drawerInvoice.studentName}
                </p>}
            </div>
            <button type="button" onClick={closeDrawer} className={`p-2 rounded-full ${isDark ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close panel" data-id="element-5515">
              <XIcon className="h-5 w-5" data-id="element-5516" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-id="element-5517">
            {/* Billing Type */}
            <div data-id="element-5518">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5519">
                Billing Type
              </label>
              <div className="relative" ref={billingTypeDropdownRef} data-id="element-5520">
                <button type="button" onClick={() => setIsBillingTypeDropdownOpen(!isBillingTypeDropdownOpen)} className={`invoice-dropdown-trigger flex items-center justify-between w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-0 sm:text-sm ${isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus:border-gray-300' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:border-gray-500'}`} data-id="element-5521">
                  <span data-id="element-5522">{drawerFormValues.billingType}</span>
                  <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform duration-200 ${isBillingTypeDropdownOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5523" />
                </button>
                {isBillingTypeDropdownOpen && <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-300'}`} onMouseLeave={() => setHoveredBillingTypeOption(null)} data-id="element-5524">
                    {billingTypeOptions.map(option => {
                  const isHovered = hoveredBillingTypeOption === option;
                  const isSelected = drawerFormValues.billingType === option;
                  const showHighlight = hoveredBillingTypeOption !== null ? isHovered : isSelected;
                  return <button key={option} type="button" onMouseEnter={() => setHoveredBillingTypeOption(option)} onClick={() => {
                    setDrawerFormValues(prev => ({
                      ...prev,
                      billingType: option as 'Monthly' | 'Per Lesson'
                    }));
                    setIsBillingTypeDropdownOpen(false);
                    setHoveredBillingTypeOption(null);
                  }} className={`invoice-dropdown-item block w-full text-left px-3 py-2.5 sm:text-sm first:rounded-t-lg last:rounded-b-lg ${showHighlight ? isDark ? 'bg-white/10 text-white font-medium' : 'bg-gray-100 text-gray-900 font-medium' : isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5525">
                          {option}
                        </button>;
                })}
                  </div>}
              </div>
            </div>

            {/* Conditional: Billing Period (Monthly) or Lesson Date (Per Lesson) */}
            {drawerFormValues.billingType === 'Monthly' ? <div data-id="element-5526">
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5527">
                  Billing Period
                </label>
                <input type="text" name="billingPeriod" placeholder="e.g. March 2026" value={drawerFormValues.billingPeriod} onChange={handleDrawerInputChange} className={`w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-0 ${isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus:border-gray-300 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:border-gray-500 placeholder-gray-400'}`} data-id="element-5528" />
              </div> : <div data-id="element-5529">
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5530">
                  Lesson Date
                </label>
                <input type="date" name="lessonDate" value={drawerFormValues.lessonDate} onChange={handleDrawerInputChange} className={`w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-0 ${isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus:border-gray-300' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:border-gray-500'}`} data-id="element-5531" />
              </div>}

            <div data-id="element-5532">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5533">
                Student
              </label>
              <div className="relative" ref={studentDropdownRef} data-id="element-5534">
                <button type="button" onClick={() => setIsStudentDropdownOpen(!isStudentDropdownOpen)} className={`invoice-dropdown-trigger flex items-center justify-between w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-0 sm:text-sm ${formErrors.studentId ? 'border-red-500' : isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus:border-gray-300' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:border-gray-500'} ${formErrors.studentId ? '' : isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5535">
                  <span className={drawerFormValues.studentName ? '' : isDark ? 'text-gray-500' : 'text-gray-400'} data-id="element-5536">
                    {drawerFormValues.studentName || 'Select a student'}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform duration-200 ${isStudentDropdownOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5537" />
                </button>
                {formErrors.studentId && <p className="mt-1 text-xs text-red-500" data-id="element-5538">
                    {formErrors.studentId}
                  </p>}
                {isStudentDropdownOpen && <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-300'}`} data-id="element-5539">
                    <div className="p-2" data-id="element-5540">
                      <input type="text" placeholder="Search students..." value={studentSearchQuery} onChange={e => setStudentSearchQuery(e.target.value)} className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-0 ${isDark ? 'bg-navy-900 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}`} data-id="element-5541" />
                    </div>
                    <div className="max-h-60 overflow-auto" data-id="element-5542">
                      {isStudentsLoading ? <div className="flex items-center justify-center py-4" data-id="element-5543">
                          <LoaderIcon className={`h-5 w-5 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5544" />
                        </div> : studentsError ? <div className={`px-3 py-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`} data-id="element-5545">
                          {studentsError}
                        </div> : students.filter(s => s.name.toLowerCase().includes(studentSearchQuery.toLowerCase())).length === 0 ? <div className={`px-3 py-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5546">
                          No students found
                        </div> : students.filter(s => s.name.toLowerCase().includes(studentSearchQuery.toLowerCase())).map(student => <button key={student.id} type="button" onClick={() => {
                    setDrawerFormValues(prev => ({
                      ...prev,
                      studentId: student.id,
                      studentName: student.name
                    }));
                    setIsStudentDropdownOpen(false);
                    setStudentSearchQuery('');
                    if (formErrors.studentId) {
                      setFormErrors(prev => ({
                        ...prev,
                        studentId: undefined
                      }));
                    }
                  }} className={`invoice-dropdown-item block w-full text-left px-3 py-2.5 sm:text-sm first:rounded-t-lg last:rounded-b-lg ${drawerFormValues.studentId === student.id ? isDark ? 'bg-white/10 text-white font-medium' : 'bg-gray-100 text-gray-900 font-medium' : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-5547">
                              {student.name}
                            </button>)}
                    </div>
                  </div>}
              </div>
            </div>

            <div data-id="element-5548">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5549">
                Amount
              </label>
              <div className={`flex items-center w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus-within:ring-0 ${formErrors.amount ? 'border-red-500' : isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus-within:border-gray-300' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus-within:border-gray-500'} ${formErrors.amount ? '' : isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5550">
                <span className={`mr-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5551">
                  $
                </span>
                <input type="number" name="amount" value={drawerFormValues.amount} onChange={handleDrawerInputChange} className="w-full bg-transparent border-none p-0 focus:ring-0 focus:outline-none outline-none" step="0.01" data-id="element-5552" />
              </div>
              {formErrors.amount && <p className="mt-1 text-xs text-red-500" data-id="element-5553">{formErrors.amount}</p>}
            </div>

            <div data-id="element-5554">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5555">
                Date
              </label>
              <input type="date" name="date" value={drawerFormValues.date} onChange={handleDrawerInputChange} className={`w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-0 ${isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus:border-gray-300' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:border-gray-500'}`} data-id="element-5556" />
            </div>

            <div data-id="element-5557">
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5558">
                Status
              </label>
              <div className="relative" ref={drawerStatusDropdownRef} data-id="element-5559">
                <button type="button" onClick={() => setIsDrawerStatusDropdownOpen(!isDrawerStatusDropdownOpen)} className={`invoice-dropdown-trigger flex items-center justify-between w-full px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-0 sm:text-sm ${isDark ? 'bg-navy-800 border-gray-500 text-white hover:border-gray-400 focus:border-gray-300' : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:border-gray-500'}`} data-id="element-5560">
                  <span data-id="element-5561">{drawerFormValues.status}</span>
                  <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform duration-200 ${isDrawerStatusDropdownOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-5562" />
                </button>
                {isDrawerStatusDropdownOpen && <div className={`absolute z-10 mt-1 w-full rounded-lg border shadow-lg ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-300'}`} onMouseLeave={() => setHoveredDrawerStatusOption(null)} data-id="element-5563">
                    {drawerStatusOptions.map(option => {
                  const isHovered = hoveredDrawerStatusOption === option;
                  const isSelected = drawerFormValues.status === option;
                  const showHighlight = hoveredDrawerStatusOption !== null ? isHovered : isSelected;
                  return <button key={option} type="button" onMouseEnter={() => setHoveredDrawerStatusOption(option)} onClick={() => {
                    setDrawerFormValues(prev => ({
                      ...prev,
                      status: option
                    }));
                    setIsDrawerStatusDropdownOpen(false);
                    setHoveredDrawerStatusOption(null);
                  }} className={`invoice-dropdown-item block w-full text-left px-3 py-2.5 sm:text-sm first:rounded-t-lg last:rounded-b-lg ${showHighlight ? isDark ? 'bg-white/10 text-white font-medium' : 'bg-gray-100 text-gray-900 font-medium' : isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-5564">
                          {option}
                        </button>;
                })}
                  </div>}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-6 py-4 border-t flex items-center ${isDark ? 'border-navy-700 bg-navy-800' : 'border-gray-200 bg-white'}`} data-id="element-5565">
            {drawerMode === 'edit' && <button type="button" onClick={handleDrawerDelete} disabled={isSaving} className={`px-3 py-2 rounded-md text-sm font-medium text-red-500 hover:bg-red-500/10 active:bg-red-500/20 transition-colors ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-5566">
                Delete Invoice
              </button>}
            <div className="ml-auto flex gap-3" data-id="element-5567">
              <button type="button" onClick={closeDrawer} disabled={isSaving} className={`px-4 py-2 rounded-md font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-100'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-5568">
                Cancel
              </button>
              <button type="button" onClick={handleDrawerSave} disabled={isSaving} className={`flex items-center px-4 py-2 border-2 rounded-md font-medium transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 active:bg-[#10B981]/20' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5 active:bg-[#10B981]/10'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-5569">
                {isSaving && <LoaderIcon className="w-4 h-4 mr-2 animate-spin" data-id="element-5570" />}
                {drawerMode === 'create' ? 'Create Invoice' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={confirmDelete} title="Delete Invoice" message={`Are you sure you want to delete this invoice${drawerInvoice?.studentName ? ` for ${drawerInvoice.studentName}` : ''}? This action cannot be undone.`} confirmText="Delete" cancelText="Cancel" darkMode={isDark} isDeleting={isSaving} data-id="element-5571" />
    </>;
};